import { useEffect, useState } from "react";
import { clearToken, getApiBase, getToken, setApiBase, setToken } from "./auth.ts";
import { WhyPraxis } from "./pages/WhyPraxis.tsx";
import { StartHere } from "./pages/StartHere.tsx";
import { Connect } from "./pages/Connect.tsx";
import { Dashboard } from "./pages/Dashboard.tsx";
import { Policies } from "./pages/Policies.tsx";
import { Intents } from "./pages/Intents.tsx";
import { Audit } from "./pages/Audit.tsx";

type Route = "why" | "guide" | "connect" | "dashboard" | "policies" | "intents" | "audit";
const ROUTES: Route[] = ["why", "guide", "connect", "dashboard", "policies", "intents", "audit"];
const ROUTE_LABEL: Record<Route, string> = {
  why: "Why Praxis",
  guide: "Start here",
  connect: "Connect",
  dashboard: "dashboard",
  policies: "policies",
  intents: "intents",
  audit: "audit",
};

const NAV_GROUPS: { label: string; routes: Route[] }[] = [
  { label: "Learn", routes: ["why", "guide", "connect"] },
  { label: "Operate", routes: ["dashboard", "policies", "intents", "audit"] },
];

function currentRoute(): Route {
  const h = window.location.hash.replace(/^#\/?/, "") as Route;
  return ROUTES.includes(h) ? h : "why";
}

export function App() {
  const [authed, setAuthed] = useState<boolean>(() => getToken() !== "");
  const [route, setRoute] = useState<Route>(currentRoute());

  useEffect(() => {
    const on = () => setRoute(currentRoute());
    window.addEventListener("hashchange", on);
    return () => window.removeEventListener("hashchange", on);
  }, []);

  if (!authed) return <Login onAuthed={() => setAuthed(true)} />;

  return (
    <div className="app">
      <aside className="sidebar">
        <a className="brand" href="#/why">
          <svg width="22" height="22" viewBox="0 0 20 20" fill="none" aria-hidden="true">
            <defs>
              <linearGradient id="side-logo" x1="0" y1="0" x2="20" y2="20">
                <stop offset="0%" stopColor="#ff6a5e" />
                <stop offset="100%" stopColor="#f8392f" />
              </linearGradient>
            </defs>
            <circle cx="10" cy="10" r="8" stroke="url(#side-logo)" strokeWidth="1.6" />
            <circle cx="10" cy="10" r="3" fill="url(#side-logo)" />
          </svg>
          {/* prettier-ignore */}
          <span className="brand-word">Prax<span className="lpx-i"><span className="lpx-i-dot" /></span>s</span>
        </a>
        <nav>
          {NAV_GROUPS.map((g) => (
            <div className="nav-group" key={g.label}>
              <span className="nav-label">{g.label}</span>
              {g.routes.map((r) => (
                <a key={r} href={`#/${r}`} className={route === r ? "active" : ""}>
                  {ROUTE_LABEL[r]}
                </a>
              ))}
            </div>
          ))}
        </nav>
        <button
          className="ghost logout"
          onClick={() => {
            clearToken();
            setAuthed(false);
          }}
        >
          Log out
        </button>
      </aside>
      <main className="content">
        {route === "why" && <WhyPraxis />}
        {route === "guide" && <StartHere />}
        {route === "connect" && <Connect />}
        {route === "dashboard" && <Dashboard />}
        {route === "policies" && <Policies />}
        {route === "intents" && <Intents />}
        {route === "audit" && <Audit />}
      </main>
    </div>
  );
}

function Login({ onAuthed }: { onAuthed: () => void }) {
  const [token, setTok] = useState("");
  const [base, setBase] = useState(getApiBase());

  return (
    <div className="login">
      <form
        className="login-card"
        onSubmit={(e) => {
          e.preventDefault();
          if (!token.trim()) return;
          setApiBase(base);
          setToken(token);
          onAuthed();
        }}
      >
        <div className="login-brand">
          <svg width="26" height="26" viewBox="0 0 20 20" fill="none" aria-hidden="true">
            <defs>
              <linearGradient id="login-logo" x1="0" y1="0" x2="20" y2="20">
                <stop offset="0%" stopColor="#ff6a5e" />
                <stop offset="100%" stopColor="#f8392f" />
              </linearGradient>
            </defs>
            <circle cx="10" cy="10" r="8" stroke="url(#login-logo)" strokeWidth="1.6" />
            <circle cx="10" cy="10" r="3" fill="url(#login-logo)" />
          </svg>
          {/* prettier-ignore */}
          <span className="login-word">Prax<span className="lpx-i"><span className="lpx-i-dot" /></span>s</span>
        </div>
        <p className="login-sub">Non-custodial control plane for the Cosmos interchain</p>

        <label>
          API base <span className="muted">(blank = same origin)</span>
          <input value={base} onChange={(e) => setBase(e.target.value)} placeholder="https://praxis.malkovsky.pl" />
        </label>
        <label>
          API token
          <input type="password" value={token} onChange={(e) => setTok(e.target.value)} placeholder="bearer token" autoFocus />
        </label>
        <button type="submit" className="login-enter">
          Enter <span className="arrow">→</span>
        </button>
        <p className="login-note">The token is stored in your browser only and sent solely to the Praxis API.</p>
      </form>
      <a className="login-home" href="/">
        ← Back to home
      </a>
    </div>
  );
}
